'use client';

import { useTranslations } from 'next-intl';

import { css, cx } from '@flipster/styled-system/css';

import { NumberInputWithFrame } from '../../../input/number-input-with-frame';
import type { InlineBasisMode } from '../../calculator/tpsl-basis';
import { BasisSelect } from './basis-select';
import { getInlinePlaceholderKey, modalSecondaryPlaceholder } from './placeholders';
import type { TpslBasisFieldProps } from './types';

export type { TpslBasisFieldProps, TpslBasisMode } from './types';
export { inlineBasisModes, modalBasisModes } from './types';
export { BasisSelect } from './basis-select';
export {
  formatModalSecondaryDisplay,
  getInlinePlaceholderKey,
  inlinePlaceholderKeyByMode,
  modalSecondaryPlaceholder,
} from './placeholders';
export {
  getExpectedModalSecondarySign,
  isOppositeSecondarySign,
  normalizeInlineRoiPnlSecondary,
  normalizeModalSecondaryByExpectedSign,
} from './signed-secondary';

function isInlineBasisMode(mode: string): mode is InlineBasisMode {
  return mode === 'price' || mode === 'roi' || mode === 'pnl';
}

/**
 * Number input + basis select (Price / Change / ROI / P&L).
 * Canonical absolute price stays in the parent; this field shows the active basis draft.
 *
 * Inline: Enter_* placeholders; ROI/PnL TP unsigned / SL force-negative.
 * Modal wiring prefers `ModalPriceBasisRow` (expectedSign + sign placeholder).
 */
export const TpslBasisField = ({
  mode,
  modes,
  value,
  onValueChange,
  onModeChange,
  quoteCurrency,
  precision,
  leg,
  placeholder,
  disabled,
  showDescriptions = true,
  css: cssProps,
  onFocus,
  onBlur,
  inputRef,
}: TpslBasisFieldProps) => {
  const t = useTranslations();

  const isInlineSurface = modes.includes('price');
  const resolvedPlaceholder =
    placeholder ??
    (isInlineSurface && isInlineBasisMode(mode) ? t(getInlinePlaceholderKey(mode)) : modalSecondaryPlaceholder);
  const isInlineRoiOrPnl = isInlineSurface && (mode === 'roi' || mode === 'pnl');
  const withPlus = false;
  const forceNegative = isInlineRoiOrPnl && leg === 'sl';

  return (
    <NumberInputWithFrame
      ref={inputRef}
      precision={precision}
      value={value}
      onChange={onValueChange}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={resolvedPlaceholder}
      disabled={disabled}
      withPlus={withPlus}
      forceNegative={forceNegative}
      className={cx(css(cssProps))}
      suffix={
        <div className={css({ pr: 0 })}>
          <BasisSelect
            mode={mode}
            modes={modes}
            onModeChange={onModeChange}
            quoteCurrency={quoteCurrency}
            showDescriptions={showDescriptions}
            disabled={disabled}
          />
        </div>
      }
    />
  );
};
